/**
 * Discovery admin (CHAP-593): local types for the admin API, and the pure
 * rules the page applies before it calls anything.
 *
 * The server stream implements the admin API; these types mirror the plan's
 * contract as written (Claude Outputs/plans/2026-10-16-plan-discovery-sender.md
 * in the 1116 repo). Where the plan leaves a response shape open (status,
 * import, seed), the page reads it through the tolerant normalisers below:
 * camelCase or snake_case, a gates array or a gates map. The import result is
 * shown as numbers only, so no row of the list can reach the screen even if a
 * response carried one.
 *
 * Nothing here talks to the network; the hooks in useDiscovery.ts do.
 */
import { ApiError } from '../../lib/api.js';

export type EmailNo = 1 | 2 | 3 | 4 | 5;
export type Variant = 'ask' | 'no_ask';

export const EMAIL_NOS: readonly EmailNo[] = [1, 2, 3, 4, 5];
export const VARIANTS: ReadonlyArray<{ value: Variant; label: string }> = [
  { value: 'ask', label: 'With the question (ask)' },
  { value: 'no_ask', label: 'Retired group, same words as ask (no_ask)' },
];

/** Release refuses a batch over this size (the plan's "the batch is over 200"). */
export const MAX_BATCH = 200;

// ---------------------------------------------------------------------------
// Gates
// ---------------------------------------------------------------------------

export const GATE_KEYS = [
  'approved_sha_e1',
  'approved_sha_e2',
  'approved_sha_e3',
  'approved_sha_e4',
  'approved_sha_e5',
  'postal_address',
  'fill_e3_n',
  'fill_e5_date',
  'seed_checked_e1',
  'seed_checked_e2',
  'seed_checked_e3',
  'seed_checked_e4',
  'seed_checked_e5',
  'forwarding_proven',
  'foundation_ok',
  'halt',
] as const;
export type GateKey = (typeof GATE_KEYS)[number];

export interface GateDef {
  key: GateKey;
  label: string;
  /**
   * sha: an approved copy sha, typed from Monk's approval package.
   * seed: the uuid of a seed batch that was read in a real inbox.
   * text: the postal address. note: free text saying how it was proven.
   * fill: the value a placeholder in an email's words takes, [N] or [DATE].
   */
  kind: 'sha' | 'seed' | 'text' | 'note' | 'fill';
  hint: string;
  emailNo?: EmailNo;
  /** Halt is the one gate that is good when it is NOT set. */
  inverted?: boolean;
}

const APPROVED_HINT =
  "The copy sha from Monk's approval package: 64 hex characters. This page never shows the build's own sha; " +
  'it only says whether what you type matches it.';
const SEED_HINT =
  'The id of the seed batch you read in your own inbox. Use Mark checked on its row under Dispatches, or paste its id here.';
export const HALT_RESUMES = 'Clearing halt resumes any released batch immediately.';
const FILL_HINT =
  "It becomes part of the email's words, so the email's copy sha changes: approve and seed the filled words before release. " +
  'Prepare, seed and release refuse the email while this is empty.';

export const GATES: readonly GateDef[] = [
  { key: 'approved_sha_e1', label: 'Email 1: copy approved', kind: 'sha', emailNo: 1, hint: APPROVED_HINT },
  { key: 'approved_sha_e2', label: 'Email 2: copy approved', kind: 'sha', emailNo: 2, hint: APPROVED_HINT },
  { key: 'approved_sha_e3', label: 'Email 3: copy approved', kind: 'sha', emailNo: 3, hint: APPROVED_HINT },
  { key: 'approved_sha_e4', label: 'Email 4: copy approved', kind: 'sha', emailNo: 4, hint: APPROVED_HINT },
  { key: 'approved_sha_e5', label: 'Email 5: copy approved', kind: 'sha', emailNo: 5, hint: APPROVED_HINT },
  { key: 'postal_address', label: 'Postal address in the footer', kind: 'text',
    hint: 'The full mailing address with its ZIP. Brackets, TBD, "placeholder" or "to be confirmed" are refused.' },
  { key: 'fill_e3_n', label: 'Email 3: [N], how many have answered', kind: 'fill',
    hint: `The count for "So far, [N] of you have told us...", in digits, counted the morning email 3 goes. ${FILL_HINT}` },
  { key: 'fill_e5_date', label: 'Email 5: [DATE], the board date', kind: 'fill',
    hint: `The confirmed board date as "On [DATE] we will share an update..." should read, for example "Tuesday, December 8". ${FILL_HINT}` },
  { key: 'seed_checked_e1', label: 'Email 1: seed checked', kind: 'seed', emailNo: 1, hint: SEED_HINT },
  { key: 'seed_checked_e2', label: 'Email 2: seed checked', kind: 'seed', emailNo: 2, hint: SEED_HINT },
  { key: 'seed_checked_e3', label: 'Email 3: seed checked', kind: 'seed', emailNo: 3, hint: SEED_HINT },
  { key: 'seed_checked_e4', label: 'Email 4: seed checked', kind: 'seed', emailNo: 4, hint: SEED_HINT },
  { key: 'seed_checked_e5', label: 'Email 5: seed checked', kind: 'seed', emailNo: 5, hint: SEED_HINT },
  { key: 'forwarding_proven', label: 'campaigns@ and fundraising@ forwarding proven', kind: 'note',
    hint: 'How it was proven, for example "tested from an outside address on 21 Oct".' },
  { key: 'foundation_ok', label: 'Foundation OK to email the list', kind: 'note',
    hint: "Where Nick's written OK is, for example \"Nick's email of 23 Oct\"." },
  { key: 'halt', label: 'Halt', kind: 'note', inverted: true,
    hint: `Why sending is halted. While this is set, nothing sends. ${HALT_RESUMES}` },
];

export interface GateState {
  value: string | null;
  setBy: string | null;
  setAt: string | null;
}

/** Whether a gate holds a value at all. */
export function gateIsSet(state: GateState | undefined): boolean {
  return !!state && state.value !== null && state.value.trim() !== '';
}

/** What the page knows that a gate's readiness depends on. */
export interface GateContext {
  /** The current build's copy sha for the gate's email; compared, never shown. */
  buildSha?: string | null;
  dispatches?: readonly Dispatch[];
  /** The postal_address gate's value, which a checked seed must have gone out with. */
  postal?: string | null;
  /** The versions a batch will hold, each needing its own checked seed. */
  versionsNeeded?: readonly Variant[];
}

/**
 * Whether a gate is satisfied as release would judge it: set, except halt,
 * which must be clear; the postal address must not be a placeholder; an
 * approved sha must match this build; a checked seed must be a delivered
 * seed of this build, sent with this address.
 */
export function gateReady(def: GateDef, state: GateState | undefined, ctx: GateContext = {}): boolean {
  if (def.inverted) return !gateIsSet(state);
  if (!gateIsSet(state)) return false;
  const value = state!.value!;
  if (def.key === 'postal_address') return postalProblem(value) === null;
  if (def.kind === 'sha') return approvalMatch(value, ctx.buildSha) !== 'mismatch';
  if (def.kind === 'seed') return seedCheckProblem(value, def.emailNo ?? null, ctx) === null;
  return true;
}

// ---------------------------------------------------------------------------
// Writing a gate
// ---------------------------------------------------------------------------

/** POST /discovery/gates takes { key, value }, and an empty value clears the gate. */
export interface GateWrite {
  key: GateKey;
  value: string;
}

export function gateSetBody(key: GateKey, value: string): GateWrite {
  return { key, value: value.trim() };
}

export function gateClearBody(key: GateKey): GateWrite {
  return { key, value: '' };
}

/**
 * The question asked before a gate is cleared. Clearing halt says what it
 * sets off; clearing an approval says it halts a batch of that email that is
 * sending (the server's approval_revoked).
 */
export function clearGatePrompt(def: GateDef): string {
  if (def.key === 'halt') return `Clear halt? ${HALT_RESUMES}`;
  if (def.kind === 'sha') {
    return `Clear "${def.label}"? If email ${def.emailNo} is sending, it halts at its next send, and nothing more goes until it is approved again and halt is cleared.`;
  }
  return `Clear "${def.label}"?`;
}

/** The Clear button: asks first, then writes the clearing body. True when it wrote. */
export function clearGate(def: GateDef, write: (body: GateWrite) => void, ask: (question: string) => boolean): boolean {
  if (!ask(clearGatePrompt(def))) return false;
  write(gateClearBody(def.key));
  return true;
}

/**
 * Whether a typed sha matches this build, without ever saying what this
 * build's sha is. Null when there is nothing to compare.
 */
export function approvalMatch(typed: string | null | undefined, buildSha: string | null | undefined): 'match' | 'mismatch' | null {
  const t = (typed ?? '').trim().toLowerCase();
  if (!t || !buildSha) return null;
  return t === buildSha.toLowerCase() ? 'match' : 'mismatch';
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export interface CountRow {
  label: string;
  value: number;
}

export interface Dispatch {
  id: string;
  emailNo: number | null;
  kind: string;
  variant: string | null;
  state: string;
  frozenCount: number | null;
  copySha: string | null;
  createdAt: string | null;
  releasedBy: string | null;
  releasedAt: string | null;
  typedCount: number | null;
  /** The postal address the batch went out with. */
  postalSnapshot: string | null;
  /** Sends by state for this dispatch (queued, accepted, failed...). Numbers only. */
  sends: CountRow[];
}

/** One line of the server's configuration readout: booleans, numbers and short tags only. */
export interface ConfigRow {
  key: string;
  label: string;
  value: string;
  /** true and false colour the dot; null is informational. */
  ok: boolean | null;
}

export interface DiscoveryStatus {
  counts: CountRow[];
  gates: Record<GateKey, GateState>;
  /** Gate keys the server sent that this page does not know, shown read-only. */
  otherGates: Array<{ key: string } & GateState>;
  dispatches: Dispatch[];
  config: ConfigRow[];
  /**
   * The server's configuration gaps, as codes (config_sendgrid,
   * config_booking_gift_host...). Null when the server sent no list, which is
   * not the same as an empty one.
   */
  configGaps: string[] | null;
  /** The current copy sha of each email's words: what approved_sha_eN records. */
  copyShas: Partial<Record<EmailNo, string>>;
  /** Opaque recipient ids whose confirmation or notices did not go, for retry-notices. */
  retryable: string[];
  /**
   * The versions a batch is expected to hold, so the versions that each need a
   * checked seed: ask always, no_ask when anyone on the list gets it.
   */
  versionsNeeded: Variant[];
}

/** POST /discovery/prepare returns { dispatchId, frozenCount, copySha, suppressionsBackfilled }. */
export interface PrepareResult {
  dispatchId: string;
  frozenCount: number;
  copySha: string | null;
  /** Unsubscribes recorded again before the freeze; null when the server did not say. */
  suppressionsBackfilled: number | null;
}

type Obj = Record<string, unknown>;

function obj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

function str(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'boolean') return String(v);
  return null;
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) return Number(v.trim());
  return null;
}

function pick(o: Obj, ...keys: string[]): unknown {
  for (const k of keys) if (k in o) return o[k];
  return undefined;
}

/**
 * A map's own entry for a key, never one inherited from Object.prototype: a
 * code, a label path or a halt reason a person typed could be "constructor"
 * or "__proto__", and the inherited value is a function or an object, which
 * React cannot render.
 */
function own<T>(map: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

/** "no_ask" -> "No ask"; "frozenCount" -> "Frozen count". */
export function humanize(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Every finite number in an object, one level of nesting deep, and nothing
 * else. Strings, arrays and deeper objects are ignored on purpose: this is how
 * the page keeps "counts only" true whatever a response carries.
 */
export function numericLeaves(raw: unknown, labels: Readonly<Record<string, string>> = {}): CountRow[] {
  return leaves(raw, labels, '', '');
}

// `labels` maps a key path ("sameNameRows", "skipped.duplicate_zeta") to the
// words to show for it; anything unlisted is humanized.
function leaves(raw: unknown, labels: Readonly<Record<string, string>>, prefix: string, path: string): CountRow[] {
  const o = obj(raw);
  if (!o) return [];
  const rows: CountRow[] = [];
  for (const [k, v] of Object.entries(o)) {
    const keyPath = path ? `${path}.${k}` : k;
    const label = own(labels, keyPath) ?? (prefix ? `${prefix}: ${humanize(k)}` : humanize(k));
    if (typeof v === 'number' && Number.isFinite(v)) rows.push({ label, value: v });
    else if (!prefix && obj(v)) rows.push(...leaves(v, labels, label, keyPath));
  }
  return rows;
}

/** The import result's counts in plain words. Numbers only, like every count here. */
const IMPORT_LABELS: Readonly<Record<string, string>> = {
  rows: 'Rows read',
  inserted: 'New',
  updated: 'Updated',
  eligible: 'Eligible',
  notInList: 'No longer on the list',
  linkedAlumni: 'Linked to alumni records',
  sameNameRows: 'Same name as an earlier row: check by hand',
  'excluded.dnc': 'Excluded: do not contact',
  'excluded.exclude': 'Excluded: grouped exclude',
  'excluded.priority_foundation': 'Excluded: priority brother (no_ask)',
  'excluded.suppressed': 'Excluded: suppressed',
  'excluded.already_lead': 'Excluded: already a lead',
  'skipped.no_email': 'Skipped: no email',
  'skipped.invalid_email': 'Skipped: invalid email',
  'skipped.duplicate': 'Skipped: same email again',
  'skipped.duplicate_zeta': 'Skipped: same Zeta number again',
  'skipped.invalid_group': 'Skipped: unknown group',
  'skipped.no_name': 'Skipped: no first name',
  seedInboxesOnList: 'Seed inboxes on the list',
  suppressionsBackfilled: 'Unsubscribes recorded again',
};

export function importCounts(raw: unknown): CountRow[] {
  const o = obj(raw);
  // Unsubscribes recorded again (written for brothers who unsubscribed while
  // the key was changed in place) show only when there were any.
  const quiet = o && num(o.suppressionsBackfilled) === 0 ? { ...o, suppressionsBackfilled: undefined } : raw;
  return numericLeaves(quiet, IMPORT_LABELS);
}

/** The line for unsubscribes the server recorded again; null when there were none. */
export function backfillText(count: number | null | undefined): string | null {
  return typeof count === 'number' && Number.isInteger(count) && count > 0
    ? `Unsubscribes recorded again: ${count}.`
    : null;
}

/** What the page keeps of an import: its counts, and how many seed inboxes are now on the list. */
export interface ImportOutcome {
  counts: CountRow[];
  /** Seed inboxes that are brothers on the list (seed and prepare refuse while above zero); null when not said. */
  seedInboxesOnList: number | null;
}

export function readImportResult(raw: unknown): ImportOutcome {
  const o = obj(raw);
  return {
    counts: importCounts(raw),
    seedInboxesOnList: o ? num(pick(o, 'seedInboxesOnList', 'seed_inboxes_on_list')) : null,
  };
}

/** The line shown after an import that put seed inboxes on the list; null when none are. */
export function seedInboxWarning(count: number | null | undefined): string | null {
  if (count === null || count === undefined || !Number.isInteger(count) || count <= 0) return null;
  return count === 1
    ? '1 seed inbox is on this list. Remove it before preparing.'
    : `${count} seed inboxes are on this list. Remove them before preparing.`;
}

function toDispatch(raw: unknown): Dispatch | null {
  const o = obj(raw);
  if (!o) return null;
  const id = str(pick(o, 'id', 'dispatchId', 'dispatch_id'));
  if (!id) return null;
  return {
    id,
    emailNo: num(pick(o, 'emailNo', 'email_no')),
    kind: (str(pick(o, 'kind')) ?? '').toLowerCase(),
    variant: str(pick(o, 'variant')),
    state: (str(pick(o, 'state')) ?? '').toLowerCase(),
    frozenCount: num(pick(o, 'frozenCount', 'frozen_count')),
    copySha: str(pick(o, 'copySha', 'copy_sha256', 'copySha256', 'copy_sha')),
    createdAt: str(pick(o, 'frozenAt', 'frozen_at', 'createdAt', 'created_at')),
    releasedBy: str(pick(o, 'releasedBy', 'released_by')),
    releasedAt: str(pick(o, 'releasedAt', 'released_at')),
    typedCount: num(pick(o, 'typedCount', 'typed_count')),
    postalSnapshot: str(pick(o, 'postalSnapshot', 'postal_snapshot')),
    sends: numericLeaves(pick(o, 'sends')),
  };
}

const ENV_TAGS: Record<string, string> = { p: 'p (production)', s: 's (staging)' };

/**
 * The server's configuration readout. Booleans read as set or not set, numbers
 * as themselves, and short tags as text; anything longer is not shown, so a
 * value that should never be on screen cannot reach it by accident.
 */
function readConfig(raw: unknown): ConfigRow[] {
  const o = obj(raw);
  if (!o) return [];
  const rows: ConfigRow[] = [];
  for (const [key, v] of Object.entries(o)) {
    const label = humanize(key);
    if (key === 'sandbox' && typeof v === 'boolean') {
      rows.push({ key, label, value: v ? 'on: no real email leaves' : 'off', ok: null });
    } else if (key === 'envTag') {
      const tag = str(v);
      rows.push({ key, label, value: tag ? own(ENV_TAGS, tag) ?? tag : 'not set', ok: tag ? null : false });
    } else if (typeof v === 'boolean') {
      rows.push({ key, label, value: v ? 'set' : 'not set', ok: v });
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      rows.push({ key, label, value: String(v), ok: null });
    } else if (typeof v === 'string' && v.length <= 24) {
      rows.push({ key, label, value: v, ok: null });
    } else if (v === null) {
      rows.push({ key, label, value: 'not set', ok: false });
    }
  }
  return rows;
}

function readCopyShas(raw: unknown): Partial<Record<EmailNo, string>> {
  const o = obj(raw);
  const out: Partial<Record<EmailNo, string>> = {};
  if (!o) return out;
  for (const n of EMAIL_NOS) {
    const sha = str(pick(o, `e${n}`, String(n)));
    if (sha) out[n] = sha;
  }
  return out;
}

/** Sends by state, summed over the live dispatches. */
export function liveSendCounts(dispatches: readonly Dispatch[]): CountRow[] {
  const totals = new Map<string, number>();
  for (const d of dispatches) {
    if (d.kind !== 'live') continue;
    for (const r of d.sends) totals.set(r.label, (totals.get(r.label) ?? 0) + r.value);
  }
  return [...totals].map(([label, value]) => ({ label: `Live sends: ${label}`, value }));
}

function toGateState(v: unknown): GateState {
  const o = obj(v);
  if (!o) return { value: str(v), setBy: null, setAt: null };
  return {
    value: str(pick(o, 'value')),
    setBy: str(pick(o, 'setBy', 'set_by')),
    setAt: str(pick(o, 'setAt', 'set_at')),
  };
}

const KNOWN_GATES = new Set<string>(GATE_KEYS);

/**
 * Reads GET /discovery/status, whichever reasonable shape it arrives in. The
 * counts come from `counts` when the server sends one; otherwise from
 * `recipients` (the server stream's shape) plus the live sends summed by state.
 */
export function normaliseStatus(raw: unknown): DiscoveryStatus {
  const o = obj(raw) ?? {};
  const gates = Object.fromEntries(
    GATE_KEYS.map((k) => [k, { value: null, setBy: null, setAt: null }]),
  ) as Record<GateKey, GateState>;
  const otherGates: DiscoveryStatus['otherGates'] = [];
  const take = (key: string, v: unknown) => {
    const state = toGateState(v);
    if (KNOWN_GATES.has(key)) gates[key as GateKey] = state;
    else otherGates.push({ key, ...state });
  };
  if (Array.isArray(o.gates)) {
    for (const g of o.gates) {
      const go = obj(g);
      const key = go ? str(go.key) : null;
      if (go && key) take(key, go);
    }
  } else {
    const map = obj(o.gates);
    if (map) for (const [k, v] of Object.entries(map)) take(k, v);
  }
  const dispatches = Array.isArray(o.dispatches)
    ? o.dispatches.map(toDispatch).filter((d): d is Dispatch => d !== null)
    : [];
  const counts = obj(o.counts)
    ? numericLeaves(o.counts)
    : [...numericLeaves(o.recipients), ...liveSendCounts(dispatches)];
  const retryable = Array.isArray(o.retryable)
    ? o.retryable.filter((x): x is string => typeof x === 'string' && UUID_RX.test(x))
    : [];
  const configObj = obj(o.config);
  const configGaps =
    configObj && Array.isArray(configObj.gaps)
      ? configObj.gaps.filter((g): g is string => typeof g === 'string' && CODE_RX.test(g))
      : null;
  const recipients = obj(o.recipients);
  const noAsk = recipients ? num(pick(recipients, 'noAsk', 'no_ask')) : null;
  // A suppression key that is set but is not the one the server first used is
  // no better than none, so its row does not read as a green "set".
  const keyMismatch = configGaps?.includes('suppression_key_mismatch') ?? false;
  const config = readConfig(o.config).map((r) =>
    keyMismatch && r.key === 'suppressionKey' ? { ...r, value: 'not the original key', ok: false } : r,
  );
  return {
    counts,
    gates,
    otherGates,
    dispatches,
    config,
    configGaps,
    copyShas: readCopyShas(o.copySha ?? o.copyShas),
    retryable,
    versionsNeeded: noAsk !== null && noAsk > 0 ? ['ask', 'no_ask'] : ['ask'],
  };
}

/** A server code: lower case, digits and underscores. Anything else is not shown as one. */
const CODE_RX = /^[a-z0-9_]{1,64}$/;

/** Recipient and dispatch ids are uuids, and the server accepts nothing else. */
export const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Enough of an email address to send as one; the server decides. */
export const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Reads the prepare response; null when it lacks an id or a whole-number count. */
export function readPrepareResult(raw: unknown): PrepareResult | null {
  const o = obj(raw);
  if (!o) return null;
  const dispatchId = str(pick(o, 'dispatchId', 'dispatch_id', 'id'));
  const frozenCount = num(pick(o, 'frozenCount', 'frozen_count'));
  if (!dispatchId || frozenCount === null || !Number.isInteger(frozenCount)) return null;
  return {
    dispatchId,
    frozenCount,
    copySha: str(pick(o, 'copySha', 'copy_sha256', 'copy_sha')),
    suppressionsBackfilled: num(pick(o, 'suppressionsBackfilled', 'suppressions_backfilled')),
  };
}

/** POST /discovery/seed returns { ok, dispatchId, count, copySha }. */
export interface SeedResult {
  dispatchId: string;
  count: number | null;
  copySha: string | null;
}

export function readSeedResult(raw: unknown): SeedResult | null {
  const o = obj(raw);
  if (!o) return null;
  const dispatchId = str(pick(o, 'dispatchId', 'dispatch_id'));
  if (!dispatchId) return null;
  return { dispatchId, count: num(pick(o, 'count')), copySha: str(pick(o, 'copySha', 'copy_sha256', 'copy_sha')) };
}

/** Pulls a copy sha out of any response that carries one (seed, preview). */
export function readCopySha(raw: unknown): string | null {
  const o = obj(raw);
  return o ? str(pick(o, 'copySha', 'copy_sha256', 'copy_sha')) : null;
}

// A live dispatch is waiting for release until it is released or settled.
// The state names are the server's; anything that reads as finished or
// stopped is treated as not releasable.
const SETTLED = new Set([
  'released', 'sending', 'sent', 'done', 'complete', 'completed', 'finished',
  'stopped', 'halted', 'cancelled', 'canceled', 'failed', 'expired', 'superseded',
]);

export function isReleasable(d: Dispatch): boolean {
  return (
    d.kind === 'live' && !d.releasedBy && !d.releasedAt && d.frozenCount !== null && !SETTLED.has(d.state)
  );
}

/**
 * The batch this tab prepared, for as long as it can still be released. Once
 * the server's status lists it as anything else (cancelled, here or in another
 * tab; released; stopped), it is not offered again, and the Release panel falls
 * back to what status lists. A batch status does not list yet is kept: it was
 * frozen a moment ago.
 */
export function preparedStillReleasable<T extends { dispatchId: string }>(
  prepared: T | null,
  dispatches: readonly Dispatch[],
): T | null {
  if (!prepared) return null;
  const listed = dispatches.find((d) => d.id === prepared.dispatchId);
  return listed && !isReleasable(listed) ? null : prepared;
}

/** The newest releasable live dispatch, by frozen time when known, else the last listed. */
export function releaseCandidate(dispatches: readonly Dispatch[]): Dispatch | null {
  const waiting = dispatches.filter(isReleasable);
  if (waiting.length === 0) return null;
  const dated = waiting.filter((d) => d.createdAt && !Number.isNaN(Date.parse(d.createdAt)));
  if (dated.length === waiting.length) {
    return [...dated].sort((a, b) => Date.parse(b.createdAt!) - Date.parse(a.createdAt!))[0]!;
  }
  return waiting[waiting.length - 1]!;
}

// ---------------------------------------------------------------------------
// Seeds and released batches
// ---------------------------------------------------------------------------

/** The first 12 characters of a sha: enough to tell copies apart, too short to approve with. */
export function shaPrefix(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 12) : 'none';
}

function sendCount(d: Dispatch, state: string): number {
  return d.sends.find((r) => r.label.toLowerCase() === state)?.value ?? 0;
}

/** How many copies of a batch the mail service accepted. */
export function acceptedCount(d: Dispatch): number {
  return sendCount(d, 'accepted');
}

/** How many of a released batch are still waiting to go. */
export function unsentCount(d: Dispatch): number {
  return sendCount(d, 'queued');
}

const byNewest = (a: Dispatch, b: Dispatch) =>
  (b.createdAt ? Date.parse(b.createdAt) : 0) - (a.createdAt ? Date.parse(a.createdAt) : 0);

/**
 * The newest seed of one email (and, given one, one version) whose copy is
 * this build's, which is the only kind of seed release accepts as checked.
 * With no build sha to compare, the newest seed of that email.
 */
export function newestMatchingSeed(
  dispatches: readonly Dispatch[],
  emailNo: number,
  buildSha: string | null | undefined,
  variant?: Variant,
): Dispatch | null {
  const seeds = dispatches.filter(
    (d) =>
      d.kind === 'seed' &&
      d.emailNo === emailNo &&
      (!variant || d.variant === variant) &&
      (!buildSha || d.copySha === buildSha),
  );
  return [...seeds].sort(byNewest)[0] ?? null;
}

/** The server keeps at most this many ids in a seed_checked_eN list. */
export const MAX_CHECKED_SEEDS = 4;

/**
 * The seed batch ids a seed_checked_eN value names: comma or space
 * separated, lower case, in order, without repeats. The server reads it the
 * same way (dispatch.ts seedIdsOf).
 */
export function seedIdsOf(value: string | null | undefined): string[] {
  if (!value) return [];
  const out: string[] = [];
  for (const part of value.split(/[\s,]+/)) {
    const id = part.toLowerCase();
    if (UUID_RX.test(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** What the page says under a typed seed list the server would refuse unread. */
export const SEED_LIST_SHAPE = 'One to four seed batch ids, comma separated: each a uuid, as shown under Dispatches.';

/**
 * Why the server would refuse a typed seed_checked_eN list before it looks any
 * id up, or null when it would look them up. As the gates route: one to four
 * parts, each a uuid (else seed_not_found), and no id named twice, in any
 * case (else seed_duplicate).
 */
export function seedDraftProblem(draft: string): string | null {
  const parts = draft.split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0 || parts.length > MAX_CHECKED_SEEDS || !parts.every((p) => UUID_RX.test(p))) {
    return SEED_LIST_SHAPE;
  }
  if (seedIdsOf(draft).length !== parts.length) return refusalText('seed_duplicate');
  return null;
}

export interface SeedEntry {
  id: string;
  /** The batch the id names, when it is among the dispatches the status lists. */
  dispatch: Dispatch | null;
}

/** One entry per id in a seed_checked_eN value, each with its batch when the page knows it. */
export function seedEntries(value: string | null | undefined, dispatches: readonly Dispatch[]): SeedEntry[] {
  return seedIdsOf(value).map((id) => ({ id, dispatch: dispatches.find((d) => d.id.toLowerCase() === id) ?? null }));
}

// Why release would not take seed d as its version's checked seed, as the end
// of a sentence ("The checked ask seed ..."), or null when it would.
function seedFault(d: Dispatch, emailNo: number | null, ctx: GateContext): string | null {
  if (d.kind !== 'seed') return 'is not a seed';
  if (emailNo !== null && d.emailNo !== emailNo) return 'was for another email';
  if (ctx.buildSha && d.copySha !== ctx.buildSha) return 'is an older copy: the words have changed since';
  if (ctx.postal !== undefined && (ctx.postal ?? '') !== (d.postalSnapshot ?? '')) {
    return 'went out with a different postal address';
  }
  if (acceptedCount(d) < 1) return 'has no delivered copy yet';
  return null;
}

/**
 * Why release would refuse this seed_checked_eN list, or null if it would
 * take it. The server's test, as far as the page can see it: for every
 * version the batch holds, one listed seed of that version, of this email,
 * of this build's copy, sent with the current postal address, and delivered.
 */
export function seedCheckProblem(value: string, emailNo: number | null, ctx: GateContext): string | null {
  if (seedIdsOf(value).length === 0) return 'This is not a list of seed batch ids.';
  if (!ctx.dispatches) return null;
  const entries = seedEntries(value, ctx.dispatches);
  const problems: string[] = [];
  for (const version of ctx.versionsNeeded ?? ['ask']) {
    const listed = entries.filter((e) => e.dispatch?.variant === version);
    if (listed.some((e) => seedFault(e.dispatch!, emailNo, ctx) === null)) continue;
    problems.push(
      listed.length === 0
        ? `No ${version} seed is checked.`
        : `The checked ${version} seed ${seedFault(listed[0]!.dispatch!, emailNo, ctx)}.`,
    );
  }
  const unplaced = entries.filter((e) => !e.dispatch).length;
  if (problems.length && unplaced) {
    problems.push(`${unplaced} listed ${unplaced === 1 ? 'batch is' : 'batches are'} not among the recent dispatches.`);
  }
  return problems.length ? problems.join(' ') : null;
}

/**
 * Why release would not take seed d as its version's checked seed, as a
 * sentence, or null when it would: the seed gate's own test (this email, this
 * build's copy, the current postal address, at least one delivered). The page
 * offers Mark checked only when this is null, and shows this in its place.
 */
export function seedMarkProblem(d: Dispatch, ctx: GateContext): string | null {
  const fault = seedFault(d, d.emailNo, ctx);
  return fault ? `This seed ${fault}.` : null;
}

const sameId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** The id in the list that seed d would replace: a seed of the same version. */
function replacedBy(d: Dispatch, currentValue: string | null | undefined, dispatches: readonly Dispatch[]): string | null {
  return (
    seedIdsOf(currentValue).find(
      (id) => !sameId(id, d.id) && dispatches.some((x) => sameId(x.id, id) && x.variant === d.variant),
    ) ?? null
  );
}

/**
 * The gate write that marks seed d checked: the email's list with d in place
 * of any seed of the same version, and every other id kept, so marking one
 * version never overwrites the other's. Ids the page cannot place (not among
 * the recent dispatches) are kept too, after the ones it can, up to the
 * server's limit. Null for anything that is not a seed batch.
 */
export function seedCheckedBody(
  d: Dispatch,
  currentValue: string | null | undefined,
  dispatches: readonly Dispatch[],
): GateWrite | null {
  if (d.kind !== 'seed' || !UUID_RX.test(d.id)) return null;
  if (d.emailNo !== 1 && d.emailNo !== 2 && d.emailNo !== 3 && d.emailNo !== 4 && d.emailNo !== 5) return null;
  const replaced = replacedBy(d, currentValue, dispatches);
  const others = seedIdsOf(currentValue).filter((id) => !sameId(id, d.id) && id !== replaced);
  const known = others.filter((id) => dispatches.some((x) => sameId(x.id, id)));
  const unplaced = others.filter((id) => !known.includes(id));
  const ids = [d.id.toLowerCase(), ...known, ...unplaced].slice(0, MAX_CHECKED_SEEDS);
  return { key: `seed_checked_e${d.emailNo}` as GateKey, value: ids.join(',') };
}

export function markSeedCheckedPrompt(d: Dispatch, replaces: boolean): string {
  return (
    `Mark the email ${d.emailNo} seed checked? Version ${d.variant ?? 'unknown'}, sha ${shaPrefix(d.copySha)}, ` +
    `${acceptedCount(d)} delivered.` +
    (replaces ? ` It replaces the ${d.variant ?? 'unknown'} seed checked before; the other version stays as it is.` : '') +
    ' Only if you read this copy in your own inbox.'
  );
}

/**
 * One Mark checked at a time. Each press writes the email's whole seed list,
 * built from the list the page last read, so a second press before the status
 * has been read again would build on the old list and drop the first press's
 * seed: a lost update. A press takes this lock, and it is released once the
 * write has settled and the status has been fetched again.
 */
export interface MarkLock {
  readonly held: boolean;
  /** Takes the lock; false when it is already held. */
  take(): boolean;
  release(): void;
}

export function createMarkLock(onChange?: (held: boolean) => void): MarkLock {
  let held = false;
  return {
    get held() {
      return held;
    },
    take() {
      if (held) return false;
      held = true;
      onChange?.(true);
      return true;
    },
    release() {
      if (!held) return;
      held = false;
      onChange?.(false);
    },
  };
}

/**
 * The write a Mark checked press makes: sends the body and releases the lock
 * when the send settles, written or refused. The page's send settles only
 * after the status has been fetched again (useDiscoveryAction).
 */
export function lockedWrite(send: (body: GateWrite) => Promise<unknown>, lock: MarkLock): (body: GateWrite) => void {
  return (body) => {
    let sent: Promise<unknown>;
    try {
      sent = send(body);
    } catch {
      lock.release();
      return;
    }
    void sent.then(
      () => lock.release(),
      () => lock.release(),
    );
  };
}

/**
 * Mark checked: asks first, naming the version and sha prefix, then writes
 * the email's list with this seed for its version. With a lock, a press while
 * another is unsettled is refused before it asks, and nothing is written.
 */
export function markSeedChecked(
  d: Dispatch,
  currentValue: string | null | undefined,
  dispatches: readonly Dispatch[],
  write: (body: GateWrite) => void,
  ask: (question: string) => boolean,
  lock?: MarkLock,
): boolean {
  if (lock?.held) return false;
  const body = seedCheckedBody(d, currentValue, dispatches);
  if (!body || !ask(markSeedCheckedPrompt(d, replacedBy(d, currentValue, dispatches) !== null))) return false;
  // Taken after the question, which blocks: a declined press holds nothing.
  if (lock && !lock.take()) return false;
  write(body);
  return true;
}

/** A live batch that is frozen or sending can be cancelled, as the server allows. */
export function isCancellable(d: Dispatch): boolean {
  return d.kind === 'live' && (d.state === 'frozen' || d.state === 'sending') && UUID_RX.test(d.id);
}

/** The question before a cancel, naming the email and how many are queued. */
export function cancelPrompt(d: Dispatch): string {
  const queued = unsentCount(d);
  if (d.state === 'frozen') {
    return (
      `Cancel the frozen email ${d.emailNo} batch? Its ${queued} queued ${queued === 1 ? 'email' : 'emails'} ` +
      'will not go. Nothing from it has been sent.'
    );
  }
  return (
    `Cancel the email ${d.emailNo} batch? ${queued} of ${d.frozenCount ?? 'its'} ` +
    `${queued === 1 ? 'has' : 'have'} not gone yet, and will not go. Any already sent stay sent.`
  );
}

/** Cancel this batch: asks first, naming the email and what is left, then posts { dispatchId }. */
export function cancelBatch(
  d: Dispatch,
  post: (body: { dispatchId: string }) => void,
  ask: (question: string) => boolean,
): boolean {
  if (!isCancellable(d) || !ask(cancelPrompt(d))) return false;
  post({ dispatchId: d.id });
  return true;
}

// ---------------------------------------------------------------------------
// The release gate
// ---------------------------------------------------------------------------

export interface GateResult {
  ok: boolean;
  reason: string | null;
}

/**
 * Release stays disabled unless the typed count is exactly the frozen count.
 * Exactly means the same decimal digits: " 150 " passes, "0150", "150.0" and
 * "1 50" do not, because the point of typing it is that it was read, not
 * that it parses to the same number. The server checks it again.
 */
export function releaseGate(frozenCount: number | null | undefined, typed: string): GateResult {
  if (frozenCount === null || frozenCount === undefined || !Number.isInteger(frozenCount)) {
    return { ok: false, reason: 'Prepare a batch first.' };
  }
  if (frozenCount <= 0) return { ok: false, reason: 'The frozen batch is empty, so there is nothing to release.' };
  if (frozenCount > MAX_BATCH) {
    return { ok: false, reason: `The batch is over ${MAX_BATCH}, and the server refuses that.` };
  }
  if (typed.trim() !== String(frozenCount)) {
    return { ok: false, reason: 'Type the frozen count exactly to release.' };
  }
  return { ok: true, reason: null };
}

/** True when a frozen snapshot is older than the 24 hours release allows. */
export function snapshotIsStale(createdAt: string | null, now: number): boolean {
  if (!createdAt) return false;
  const t = Date.parse(createdAt);
  return !Number.isNaN(t) && now - t > 24 * 60 * 60 * 1000;
}

// ---------------------------------------------------------------------------
// The CSV and the postal address
// ---------------------------------------------------------------------------

export const CSV_COLUMNS = ['first', 'last', 'email', 'phone', 'zeta', 'group', 'dnc'] as const;

/** The server's limit on the csv field: 900,000 characters. */
export const MAX_CSV_CHARS = 900_000;

/** The required columns missing from the header row, lowercased; empty when all are there. */
export function csvMissingColumns(text: string): string[] {
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const header = body.split(/\r?\n/, 1)[0] ?? '';
  const cols = header.split(',').map((c) => c.trim().replace(/^"(.*)"$/, '$1').trim().toLowerCase());
  return CSV_COLUMNS.filter((c) => !cols.includes(c));
}

/**
 * Why the server would refuse this postal address, or null if it would not.
 * Mirrors the plan: empty, a placeholder ("[", TBD, "placeholder", "to be
 * confirmed") or no ZIP.
 */
export function postalProblem(address: string | null): string | null {
  const a = (address ?? '').trim();
  if (!a) return 'No postal address is set.';
  if (a.includes('[') || /\bTBD\b/i.test(a) || /placeholder/i.test(a) || /to be confirmed/i.test(a)) {
    return 'This reads as a placeholder, so release will refuse it.';
  }
  if (!/\b\d{5}(?:-\d{4})?\b/.test(a)) return 'No ZIP code found, so release will refuse it.';
  return null;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** A refusal, a configuration gap and a halt reason alike. */
const SUPPRESSION_KEY_MISMATCH =
  "The server's unsubscribe key is not the one it first used. Nothing will import or send until the original key is restored.";

/** The server's refusal codes, in words. A code not listed here is shown humanized. */
const REFUSAL_TEXT: Record<string, string> = {
  halted: 'Halt is set.',
  dispatch_not_found: 'That batch does not exist.',
  dispatch_not_live: 'That batch is not a live batch.',
  dispatch_not_frozen: 'That batch is not waiting for release.',
  copy_not_approved: "This email's copy has no approved sha.",
  copy_sha_mismatch: 'The copy is not the approved copy: it changed after approval or after the freeze.',
  postal_placeholder: 'The postal address is a placeholder.',
  postal_changed: 'The postal address changed after the batch was frozen.',
  disclosure_missing: 'The disclosure is missing from the email.',
  seed_not_checked: 'No checked seed matches this batch: same copy, same address, delivered.',
  seed_not_checked_ask:
    'No checked seed of the ask version matches this batch: same copy, same address, delivered. Seed it, read it, mark it checked.',
  seed_not_checked_no_ask:
    'No checked seed of the no_ask version matches this batch: same copy, same address, delivered. Seed it, read it, mark it checked.',
  foundation_not_ok: "The Foundation's OK to email the list is not recorded.",
  forwarding_not_proven: 'Forwarding for campaigns@ and fundraising@ has not been proven.',
  typed_count_wrong: 'The typed count is wrong.',
  snapshot_stale: 'The batch was frozen more than 24 hours ago. Prepare it again.',
  batch_empty: 'The frozen batch is empty: there is no one to send it to.',
  batch_over_200: 'The batch is over 200.',
  previous_email_not_finished:
    'The previous email has not gone to everyone yet: a batch of it is frozen or sending, or brothers remain who have not been sent it.',
  already_frozen: 'A batch for this email is already frozen.',
  already_released: 'A batch of this email is still sending. Prepare the next once it has finished.',
  limit_out_of_range: `A batch takes 1 to ${MAX_BATCH} brothers.`,
  nothing_remaining: 'Everyone this email can go to has been sent it: there is no one left for another batch.',
  concurrent_freeze: 'Another freeze ran at the same moment. Refresh and look again.',
  dispatch_not_cancellable: 'That batch is neither frozen nor sending, so there is nothing to cancel.',
  sha_malformed: 'That is not a sha: it must be 64 hex characters.',
  // mark-replied (a 404 error) and retry-notices (a refusal).
  recipient_not_found: "That brother isn't on the list any more.",
  // retry-notices: nothing was answered, so no confirmation or notice is owed.
  not_answered: "He hasn't answered yet, so there is nothing to send again.",
  // retry-notices: a newer answer arrived while it ran, with follow-ups of its own.
  answer_changed: 'His answer changed while you were looking. Reload and try again.',
  seed_not_found: 'No seed batch has that id.',
  seed_duplicate: 'That seed batch is already in the list.',
  seed_wrong_email: 'That seed was for a different email.',
  seed_inbox_on_list:
    'A seed inbox is also a brother on the list, so he would get the seed and the real email. Take it out of the seed inboxes or the list first.',
  no_ask_rows_present:
    'A priority brother (a no_ask row) is still eligible, and priority brothers get no emails. Import the list again so they are left out.',
  placeholders_unfilled_e3: 'Email 3 still holds [N]. Fill it under Gates, then approve and seed the filled words.',
  placeholders_unfilled_e5: 'Email 5 still holds [DATE]. Fill it under Gates, then approve and seed the filled words.',
  fill_malformed: '[N] takes a count in digits, and [DATE] a date as the email should read it, such as Tuesday, December 8.',
  config_token_key: 'The token key is not set.',
  config_env_tag: 'The environment tag is not set.',
  config_suppression_key: 'The suppression key is not set.',
  suppression_key_mismatch: SUPPRESSION_KEY_MISMATCH,
  config_senders: 'No senders are configured.',
  config_sendgrid: 'SendGrid is not configured.',
  config_seed_inboxes: 'No seed inboxes are configured.',
  config_notice_gift_to: 'No address is set for gift notices.',
  config_notice_club_to: 'No address is set for club notices.',
  config_gift_reply_to: 'No Reply-To is set for gift confirmations.',
  config_booking_gift: 'The gift booking link is not set.',
  config_booking_gift_host: 'The gift booking link is set but refused: it must be https on calendly.com.',
  config_booking_club: 'The club booking link is not set.',
  config_booking_club_host:
    'The club booking link is set but refused: it must be https on calendar.google.com or calendar.app.google.',
};

/** A refusal or configuration code in words. Text that is not a code passes through as it is. */
export function refusalText(refusal: string): string {
  if (!CODE_RX.test(refusal)) return refusal;
  return own(REFUSAL_TEXT, refusal) ?? `${humanize(refusal)}.`;
}

/** Why the server set halt, in words. The halt gate's value is the reason. */
const HALT_TEXT: Record<string, string> = {
  stopped: 'Stopped from this page.',
  approval_revoked: 'An approval was cleared or changed while its batch was sending.',
  sendgrid_401: 'SendGrid refused the key (401). Nothing was sent.',
  sendgrid_403: 'SendGrid refused the sender (403). Nothing was sent.',
  sendgrid_429: 'SendGrid asked for a slower pace (429). Nothing was sent.',
  three_consecutive_errors: 'Three sends in a row failed or went unknown.',
  config_unset: 'Configuration became unset while sending.',
  render_failed: 'An email could not be rendered.',
  copy_changed_since_freeze: 'The words changed after the batch was frozen.',
  variant_changed_since_freeze:
    "A brother's version changed after this batch was frozen. Cancel the batch and prepare it again.",
  suppression_key_mismatch: SUPPRESSION_KEY_MISMATCH,
};

/** The halt reason in words when it is one of the server's codes; null for a reason a person typed. */
export function haltReasonText(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return own(HALT_TEXT, v) ?? null;
}

/**
 * What retry-notices did, in words, and whether that is good news. The
 * server answers { ok, confirmation, notices }: each is "already_sent", or
 * this attempt's outcome, where "sent" and "sandboxed" went, "skipped" was not
 * tried, and anything else ("failed", "unknown") did not go.
 */
export function retryNoticesOutcome(raw: unknown): { text: string; ok: boolean } {
  const o = obj(raw);
  const confirmation = o ? str(o.confirmation) : null;
  const notices = o ? str(o.notices) : null;
  if (confirmation === 'already_sent' && notices === 'already_sent') {
    return { text: 'Both messages had already gone; nothing was sent again.', ok: true };
  }
  const went = (v: string | null) => v === 'sent' || v === 'sandboxed';
  const missed = (v: string | null) => v !== null && v !== 'already_sent' && v !== 'skipped' && !went(v);
  const failed = [
    missed(confirmation) ? 'the confirmation' : null,
    missed(notices) ? 'the notices' : null,
  ].filter((x): x is string => x !== null);
  const didNotGo = failed.length ? `${failed.join(' and ')} did not go` : '';
  if (went(confirmation) || went(notices)) {
    return failed.length
      ? { text: `Sent again, but ${didNotGo}. Try again later.`, ok: false }
      : { text: 'Sent again.', ok: true };
  }
  if (failed.length) return { text: `Nothing was sent again: ${didNotGo}. Try again later.`, ok: false };
  return { text: 'The server did not say what it sent.', ok: false };
}

/** POST /discovery/cancel returns { ok, deleted, kept }. */
export function readCancelResult(raw: unknown): { deleted: number; kept: number } | null {
  const o = obj(raw);
  if (!o) return null;
  const deleted = num(o.deleted);
  const kept = num(o.kept);
  return deleted === null || kept === null ? null : { deleted, kept };
}

/**
 * A plain sentence for an API failure, using the server's own reasons when it
 * gives them: `refusals` (the server stream's release refusal) or `reasons`,
 * and the admin gate's own refusal when the caller is impersonating.
 */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = obj(err.body);
    if (body && str(body.refusal) === 'impersonating') {
      return 'Discovery is closed while you are viewing as someone else. Stop impersonating, then try again.';
    }
    const list = body ? (Array.isArray(body.refusals) ? body.refusals : body.reasons) : null;
    const raw = Array.isArray(list) ? list : [];
    const reasons = raw
      .map((r) => (typeof r === 'string' ? refusalText(r) : obj(r) ? str((r as Obj).message) : null))
      .filter((r): r is string => !!r);
    // Counts that go with a refusal, numbers only: never a name.
    const counts = body ? numericLeaves(body.counts) : [];
    if (reasons.length) {
      return counts.length
        ? `${reasons.join(' ')} (${counts.map((c) => `${c.label}: ${c.value}`).join(', ')})`
        : reasons.join(' ');
    }
    // A known code as the error itself (mark-replied's 404 recipient_not_found).
    const code = body ? str(body.error) : null;
    const known = code && CODE_RX.test(code) ? own(REFUSAL_TEXT, code) : undefined;
    if (known) return known;
    const message = body ? str(body.message) ?? code : null;
    if (message) return `${message} (HTTP ${err.status})`;
    if (err.status === 401 || err.status === 403) return `Not allowed (HTTP ${err.status}).`;
    return `The server refused it (HTTP ${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

/**
 * A mark-replied or retry-notices failure in words. Asked by address, not
 * found means no brother on the list has that address (mistyped, or never
 * imported), which is not the same as having left the list.
 */
export function describeRecipientError(err: unknown, by: 'id' | 'email'): string {
  if (by === 'email' && err instanceof ApiError && str(obj(err.body)?.error) === 'recipient_not_found') {
    return 'No brother on the list has that address.';
  }
  return describeError(err);
}

/**
 * The import's own failures, in words. The server names the line and the
 * reason for an unreadable file (never its content), the columns a header
 * lacks, or an empty list. A refusal (409 { error: "refused", refusals }, on
 * a suppression key gap) is each refusal's sentence, with any counts.
 * Anything else falls back to describeError.
 */
export function describeImportError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = obj(err.body);
    const code = body ? str(body.error) : null;
    if (code === 'refused') return `${describeError(err)} Nothing was imported.`;
    if (body && code === 'invalid_csv') {
      const line = num(body.line);
      const reason = str(body.reason);
      return (
        `The file could not be read${line !== null ? ` at line ${line}` : ''}` +
        `${reason ? `: ${humanize(reason).toLowerCase()}` : ''}. Nothing was imported.`
      );
    }
    if (body && code === 'missing_columns') {
      const missing = Array.isArray(body.missing) ? body.missing.filter((m): m is string => typeof m === 'string') : [];
      return `The header row is missing: ${missing.join(', ') || 'a required column'}. Nothing was imported.`;
    }
    if (code === 'empty_list') return 'The file has no usable rows. Nothing was imported.';
  }
  return describeError(err);
}
